import React, { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { AlertTriangle, Loader, Trash2, X } from 'lucide-react';
import { caseApi } from '../../services/api';
import { caseApiReal } from '../../services/caseApiReal';

const CONFIRMATION_PHRASE = 'permanently delete all my cases';

/**
 * Renders the confirmation phrase such that it cannot be selected, copied,
 * dragged, or grabbed via the context menu — forcing the user to retype it
 * by hand. Each character is its own span so that even programmatic
 * select-all attempts pick up nothing useful.
 */
const UncopyablePhrase: React.FC<{ phrase: string }> = ({ phrase }) => {
  const blockClipboard = (e: React.SyntheticEvent) => {
    e.preventDefault();
    if ('clipboardData' in (e.nativeEvent as ClipboardEvent)) {
      (e.nativeEvent as ClipboardEvent).clipboardData?.setData('text/plain', '');
    }
  };

  return (
    <div
      onCopy={blockClipboard}
      onCut={blockClipboard}
      onDragStart={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
      aria-label={`Confirmation phrase: ${phrase}`}
      style={{
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
        WebkitTouchCallout: 'none',
      }}
      className="font-mono text-sm bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md px-3 py-2 text-red-800 dark:text-red-200 inline-block cursor-default"
    >
      {phrase.split('').map((ch, i) => (
        <span key={i} aria-hidden="true">
          {ch === ' ' ? '\u00A0' : ch}
        </span>
      ))}
    </div>
  );
};

export const DangerZoneSection: React.FC = () => {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [caseCount, setCaseCount] = useState<number | null>(null);
  const [countLoading, setCountLoading] = useState(false);
  const [confirmationInput, setConfirmationInput] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const matches = confirmationInput === CONFIRMATION_PHRASE;

  const openModal = async () => {
    setModalOpen(true);
    setConfirmationInput('');
    setError(null);
    setProgress(null);
    setCountLoading(true);
    try {
      const firstPage = await caseApiReal.getCases({ page: 0, size: 1 });
      setCaseCount(firstPage.totalElements);
    } catch (err) {
      console.error('Failed to load case count:', err);
      setCaseCount(null);
    } finally {
      setCountLoading(false);
    }
  };

  const closeModal = () => {
    if (deleting) return;
    setModalOpen(false);
    setConfirmationInput('');
    setError(null);
    setProgress(null);
  };

  const fetchAllCaseIds = async (): Promise<string[]> => {
    const pageSize = 100;
    const ids: string[] = [];
    let page = 0;
    while (true) {
      const result = await caseApiReal.getCases({ page, size: pageSize });
      ids.push(...result.content.map((c) => c.id));
      if (result.content.length < pageSize || ids.length >= result.totalElements) break;
      page += 1;
    }
    return ids;
  };

  const handleDeleteAll = async () => {
    if (!matches || deleting) return;
    setDeleting(true);
    setError(null);
    try {
      const ids = await fetchAllCaseIds();
      setProgress({ done: 0, total: ids.length });
      for (let i = 0; i < ids.length; i += 1) {
        await caseApi.deleteCase(ids[i]);
        setProgress({ done: i + 1, total: ids.length });
      }
      queryClient.invalidateQueries(['cases']);
      setModalOpen(false);
      setConfirmationInput('');
      setProgress(null);
      setCaseCount(0);
    } catch (err: any) {
      console.error('Failed to delete all cases:', err);
      setError(err?.message || 'Some cases could not be deleted. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    if (!modalOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !deleting) {
        setModalOpen(false);
        setConfirmationInput('');
        setError(null);
        setProgress(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modalOpen, deleting]);

  return (
    <>
      <div className="card border-red-300 dark:border-red-800/60 dark:bg-medical-800">
        <h3 className="text-lg font-medium text-red-700 dark:text-red-400 mb-4 flex items-center space-x-2">
          <AlertTriangle className="h-5 w-5" />
          <span>Danger Zone</span>
        </h3>

        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <p className="text-sm font-medium text-medical-900 dark:text-medical-100">
              Delete all cases
            </p>
            <p className="text-sm text-medical-600 dark:text-medical-400 mt-1">
              Permanently removes every case on your account, along with their documents,
              chronologies, reports, and bundles. This cannot be undone.
            </p>
          </div>
          <button
            onClick={openModal}
            className="px-4 py-2 border border-red-300 dark:border-red-800 text-red-700 dark:text-red-400 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-sm font-medium whitespace-nowrap"
          >
            Delete all cases…
          </button>
        </div>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-medical-800 rounded-lg shadow-xl max-w-lg w-full p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-medical-100">
                    Delete all cases
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-medical-400">
                    This action is permanent and cannot be undone.
                  </p>
                </div>
              </div>
              <button
                onClick={closeModal}
                disabled={deleting}
                className="text-gray-400 dark:text-medical-500 hover:text-gray-600 dark:hover:text-medical-300 transition-colors disabled:opacity-50"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-5 space-y-3 text-sm text-gray-700 dark:text-medical-300">
              <p>
                You are about to permanently delete{' '}
                <span className="font-semibold text-red-700 dark:text-red-400">
                  {countLoading ? 'all of your cases' :
                    caseCount === null ? 'all of your cases' :
                    caseCount === 0 ? 'no cases (your account has none)' :
                    `${caseCount} case${caseCount === 1 ? '' : 's'}`}
                </span>
                . Every associated document, chronology, report, bundle, and audit
                event will also be removed.
              </p>
              <p className="font-medium text-red-700 dark:text-red-400">
                There is no recovery once this completes.
              </p>
            </div>

            <div className="mb-5">
              <p className="text-sm text-gray-700 dark:text-medical-300 mb-2">
                To confirm, type the following phrase exactly. The phrase below
                cannot be selected or copied — you must type it by hand:
              </p>
              <UncopyablePhrase phrase={CONFIRMATION_PHRASE} />
              <input
                type="text"
                value={confirmationInput}
                onChange={(e) => setConfirmationInput(e.target.value)}
                onPaste={(e) => e.preventDefault()}
                placeholder="Type the phrase above"
                disabled={deleting || caseCount === 0}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                className="mt-3 w-full px-3 py-2 border border-gray-300 dark:border-medical-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent dark:bg-medical-700 dark:text-medical-100 disabled:opacity-50"
                autoFocus
              />
              {confirmationInput && !matches && (
                <p className="text-sm text-red-600 mt-2">Phrase does not match.</p>
              )}
            </div>

            {progress && (
              <div className="mb-4">
                <div className="flex justify-between text-xs text-medical-600 dark:text-medical-400 mb-1">
                  <span>Deleting cases…</span>
                  <span>{progress.done} / {progress.total}</span>
                </div>
                <div className="w-full bg-medical-200 dark:bg-medical-700 rounded-full h-2">
                  <div
                    className="bg-red-600 h-2 rounded-full transition-all"
                    style={{
                      width: `${progress.total === 0 ? 0 : (progress.done / progress.total) * 100}%`,
                    }}
                  />
                </div>
              </div>
            )}

            {error && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={closeModal}
                disabled={deleting}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-medical-600 text-gray-700 dark:text-medical-300 rounded-lg hover:bg-gray-50 dark:hover:bg-medical-700 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAll}
                disabled={!matches || deleting || caseCount === 0}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {deleting ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin" />
                    <span>Deleting…</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4" />
                    <span>Delete everything</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
